import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { generateRandomString } from "utils/helper";
import * as services from "../service/emailLoopApi";
import { setIsLoading, setEmailLoopList } from "../slices/emailLoopSlice";

function useEmailLoop() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isLoading = useSelector((state) => state.emailLoop.isLoading);
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const currentEmployee = useSelector((state) => state.company.currentEmployee);
    const emailLoopList = useSelector((state) => state.emailLoop.emailLoopList);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [memberModalOpen, setMemberModalOpen] = useState(false);
    const [memberList, setMemberList] = useState([]);
    const [memberLoading, setMemberLoading] = useState(false);

    const getEmailLoopList = useCallback(async () => {
        try {
            dispatch(setIsLoading(true));
            const response = await services.getEmailLoopList({
                requestId: generateRandomString(),
                companyId: currentCompany,
            });
            dispatch(setEmailLoopList(response.data?.content || []));
        } catch (error) {
            message.error(t("common.error"));
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch, t]);

    useEffect(() => {
        if (typeof currentCompany !== "object" && currentCompany !== null) {
            getEmailLoopList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCompany]);

    const onCreate = useCallback(() => {
        setEditingRecord(null);
        setModalOpen(true);
    }, []);

    const onEdit = useCallback((record) => {
        setEditingRecord(record);
        setModalOpen(true);
    }, []);

    const onCloseModal = useCallback(() => {
        setModalOpen(false);
        setEditingRecord(null);
    }, []);

    const onSubmit = useCallback(
        async (values) => {
            try {
                dispatch(setIsLoading(true));
                await services.saveEmailLoop({
                    id: editingRecord?.id,
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    actionId: currentEmployee,
                    groupName: values.groupName,
                    description: values.description,
                    isActive: values.isActive ? "Y" : "N",
                    memberConditionList: [
                        {
                            departmentIdList: values.departmentIdList || [],
                            positionIdList: values.positionIdList || [],
                        },
                    ],
                });
                message.success(t("common.success"));
                setModalOpen(false);
                setEditingRecord(null);
                getEmailLoopList();
            } catch (error) {
                message.error(t("common.error"));
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, currentEmployee, editingRecord, dispatch, getEmailLoopList, t]
    );

    const onStatus = useCallback(
        async (checked, record) => {
            try {
                dispatch(setIsLoading(true));
                await services.saveEmailLoop({
                    id: record.id,
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    actionId: currentEmployee,
                    isActive: checked ? "Y" : "N",
                });
                message.success(t("common.success"));
                getEmailLoopList();
            } catch (error) {
                message.error(t("common.error"));
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, currentEmployee, dispatch, getEmailLoopList, t]
    );

    const onDelete = useCallback(
        (record) => {
            Modal.confirm({
                title: t("dialog.confirmation.header"),
                content: <p>{t("dialog.confirmation.delete").replace("{entity}", record.groupName)}</p>,
                async onOk() {
                    try {
                        dispatch(setIsLoading(true));
                        await services.deleteEmailLoop({
                            id: record.id,
                            requestId: generateRandomString(),
                            companyId: currentCompany,
                            actionId: currentEmployee,
                        });
                        message.success(t("common.success"));
                        getEmailLoopList();
                    } catch (error) {
                        message.error(t("common.error"));
                    } finally {
                        dispatch(setIsLoading(false));
                    }
                },
                okText: t("common.confirm").toUpperCase(),
                cancelText: t("common.cancel").toUpperCase(),
            });
        },
        [currentCompany, currentEmployee, dispatch, getEmailLoopList, t]
    );

    // preview จำนวนสมาชิกแบบ real-time ตอนกรอกฟอร์ม (ก่อน save)
    const previewMemberCount = useCallback(
        async (departmentIdList, positionIdList) => {
            try {
                const response = await services.previewEmailLoopMember({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    memberConditionList: [{ departmentIdList, positionIdList }],
                });
                return response.data?.content || [];
            } catch (error) {
                return [];
            }
        },
        [currentCompany]
    );

    const onViewMembers = useCallback(
        async (record) => {
            try {
                setMemberLoading(true);
                setMemberModalOpen(true);
                const response = await services.getEmailLoopMemberList({
                    id: record.id,
                    requestId: generateRandomString(),
                });
                setMemberList(response.data?.content || []);
            } catch (error) {
                message.error(t("common.error"));
            } finally {
                setMemberLoading(false);
            }
        },
        [t]
    );

    return {
        emailLoopList,
        isLoading,
        modalOpen,
        editingRecord,
        memberModalOpen,
        memberList,
        memberLoading,
        onCreate,
        onEdit,
        onCloseModal,
        onSubmit,
        onStatus,
        onDelete,
        onViewMembers,
        previewMemberCount,
        setMemberModalOpen,
    };
}

export default useEmailLoop;